import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { CheckoutPromoSection } from '../components/checkout/CheckoutPromoSection';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { isEmbedded, getReturnUrl } from '../lib/embedded';
import { ArrowLeft, Check, Loader2, ShieldCheck, Globe, AlertCircle, CheckCircle, CreditCard, Zap, Wifi, Clock, Plus, Infinity } from 'lucide-react';
import { RevolutPaymentForm } from '../components/RevolutPaymentForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// All states that indicate an eSIM is expired/unusable
const EXPIRED_STATES = ['EXPIRED', 'REVOKED', 'DELETED'];

const TopUp = () => {
  const { iccid } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoBuyRef = useRef(false);
  const { user, formatPrice, convertPrice, mySims, allCountries, currentCurrency, convertToAud, getDiscountForCode } = useApp();
  const embedded = isEmbedded();
  // Native reader-app deep-link (/topup/{iccid}?buy=…&app=1) opens straight to a
  // streamlined checkout with the top-up already selected — mirroring the buy
  // flow — instead of re-showing the package picker.
  const directCheckoutRequested = embedded && !!searchParams.get('buy');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  // Find the existing eSIM and enrich with country data
  const existingEsim = useMemo(() => {
    const sim = mySims.find(sim => sim.iccid === iccid);
    if (sim) {
      // Try to find the country in allCountries for flag and isRegion info
      const countryData = allCountries.find(c => 
        c.id === sim.country_id || 
        c.name === sim.country_name ||
        c.id === sim.country?.id
      );
      return {
        ...sim,
        country: countryData || sim.country,
        isRegion: countryData?.isRegion || sim.country?.isRegion || false
      };
    }
    return sim;
  }, [mySims, iccid, allCountries]);

  // Check if eSIM is expired (only by explicit EXPIRED/REVOKED status)
  const isEsimExpired = useMemo(() => {
    if (!existingEsim) return false;
    const status = existingEsim.roamifyStatus || existingEsim.status;
    return EXPIRED_STATES.includes(status);
  }, [existingEsim]);

  // State
  const [topupPackages, setTopupPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [revolutToken, setRevolutToken] = useState(null);
  const [revolutOrderId, setRevolutOrderId] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isProcessingTopup, setIsProcessingTopup] = useState(false);
  const [topupResult, setTopupResult] = useState(null);
  const [esimUsage, setEsimUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(true);
  // Payment UX parity with the purchase checkout: multiple saved cards +
  // one-tap pay, save-card toggle, and a graceful "finalising" state when the
  // Revolut charge is still settling asynchronously.
  const [savedCard, setSavedCard] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [savedCardBusy, setSavedCardBusy] = useState(false);
  const [savedCardError, setSavedCardError] = useState('');
  const [saveCard, setSaveCard] = useState(false);
  const [cardSaveStatus, setCardSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const [provisioningPending, setProvisioningPending] = useState(false);

  // Promo code (mirrors the purchase checkout). Only percentage discounts off
  // the top-up amount are supported — referral codes still work.
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(0); // 0..1
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState(false);
  const [promoIsReferral, setPromoIsReferral] = useState(false);
  const [promoReferrerInfo, setPromoReferrerInfo] = useState(null);
  
  // Filter state
  const [dataType, setDataType] = useState('all'); // 'all', 'fixed', 'unlimited'
  const [selectedDurationIndex, setSelectedDurationIndex] = useState(0);

  // Check authentication with delay to allow localStorage to load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsCheckingAuth(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  // Redirect if not logged in (after checking)
  useEffect(() => {
    if (!isCheckingAuth && !user) {
      navigate('/login');
    }
  }, [user, navigate, isCheckingAuth]);

  // Redirect if eSIM is expired - cannot top up expired eSIMs
  useEffect(() => {
    if (!isCheckingAuth && existingEsim && isEsimExpired) {
      navigate('/dashboard', { state: { message: 'This eSIM has expired and cannot be topped up. Please purchase a new eSIM.' } });
    }
  }, [isCheckingAuth, existingEsim, isEsimExpired, navigate]);

  // Fetch real-time eSIM usage data
  useEffect(() => {
    const fetchEsimUsage = async () => {
      if (!iccid) return;
      
      setLoadingUsage(true);
      try {
        const response = await axios.get(`${API}/esim/usage/${iccid}`);
        if (response.data.success && response.data.data) {
          setEsimUsage(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching eSIM usage:', error);
      } finally {
        setLoadingUsage(false);
      }
    };

    fetchEsimUsage();
  }, [iccid]);

  // Fetch compatible top-up packages
  useEffect(() => {
    const fetchTopupPackages = async () => {
      if (!iccid) return;
      
      setLoadingPackages(true);
      try {
        const response = await axios.get(`${API}/esim/topup/packages/${iccid}`);
        // Backend always returns { success, data: { packages: [...] }, packages: [...] }.
        // The TopUp page previously assumed data was itself an array, which no
        // longer matches the backend shape and produced an empty list.
        const packages =
          (Array.isArray(response.data?.packages) && response.data.packages) ||
          (Array.isArray(response.data?.data?.packages) && response.data.data.packages) ||
          (Array.isArray(response.data?.data) && response.data.data) ||
          [];
        if (packages.length > 0) {
          // Parse and format packages according to Roamify API structure
          const formattedPackages = packages.map((pkg, idx) => {
            // Roamify shapes seen in the wild:
            //   pkg.data.dataAmount (MB) + pkg.day + pkg.package
            //   OR pkg.dataAmount (GB) + pkg.duration + pkg.name
            const dataAmountMB = pkg.data?.dataAmount;
            const dataAmountGB =
              dataAmountMB != null
                ? dataAmountMB / 1024
                : parseFloat(pkg.dataAmount || pkg.data_amount || 0) || 0;
            const days = pkg.day || pkg.duration || pkg.duration_days || pkg.days || 0;

            return {
              id: pkg.packageId || pkg.package_id || pkg.id || `topup_${idx}`,
              packageId: pkg.packageId || pkg.package_id || pkg.id,
              name: pkg.package || pkg.name || 'Top-Up Package',
              dataAmount: dataAmountGB,
              days,
              price: parseFloat(pkg.price || pkg.retailPrice || pkg.retail_price || 0) || 0,
              isUnlimited: pkg.data?.isUnlimited || pkg.isUnlimited || false,
            };
          }).filter(p => p.packageId);
          setTopupPackages(formattedPackages);
          if (formattedPackages.length > 0) {
            setSelectedPackage(formattedPackages[0]);
          }
        }
      } catch (error) {
        console.error('Error fetching top-up packages:', error);
      } finally {
        setLoadingPackages(false);
      }
    };

    fetchTopupPackages();
  }, [iccid]);

  // Helper to parse data amount from package name
  const parseDataFromName = (name) => {
    const gbMatch = name.match(/(\d+(?:\.\d+)?)\s*GB/i);
    if (gbMatch) return parseFloat(gbMatch[1]);
    const mbMatch = name.match(/(\d+(?:\.\d+)?)\s*MB/i);
    if (mbMatch) return parseFloat(mbMatch[1]) / 1000;
    return 1;
  };

  // Get available durations based on data type filter
  const availableDurations = useMemo(() => {
    let filteredByType = topupPackages;
    
    if (dataType === 'fixed') {
      filteredByType = topupPackages.filter(p => !p.isUnlimited && p.dataAmount < 999);
    } else if (dataType === 'unlimited') {
      filteredByType = topupPackages.filter(p => p.isUnlimited || p.dataAmount >= 999);
    }
    
    // Get unique durations and sort
    const durations = [...new Set(filteredByType.map(p => p.days))].sort((a, b) => a - b);
    return durations.length > 0 ? durations : [0];
  }, [topupPackages, dataType]);

  // Selected duration value
  const selectedDuration = availableDurations[selectedDurationIndex] || availableDurations[0] || 0;

  // Filtered packages based on data type and duration
  const filteredPackages = useMemo(() => {
    let result = topupPackages;
    
    // Filter by data type
    if (dataType === 'fixed') {
      result = result.filter(p => !p.isUnlimited && p.dataAmount < 999);
    } else if (dataType === 'unlimited') {
      result = result.filter(p => p.isUnlimited || p.dataAmount >= 999);
    }
    
    // Filter by duration
    if (selectedDuration > 0) {
      result = result.filter(p => p.days === selectedDuration);
    }
    
    // Sort by price
    return result.sort((a, b) => a.price - b.price);
  }, [topupPackages, dataType, selectedDuration]);

  // Update selected package when filters change
  useEffect(() => {
    if (filteredPackages.length > 0 && (!selectedPackage || !topupPackages.find(p => p.id === selectedPackage.id))) {
      setSelectedPackage(filteredPackages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPackages]);

  // Native app deep-link: /topup/{iccid}?buy={packageId} preselects that
  // top-up package so the reader-app payment lands on the chosen add-on.
  useEffect(() => {
    const buyId = searchParams.get('buy');
    if (!buyId || autoBuyRef.current || !topupPackages.length) return;
    const target = topupPackages.find(
      p => String(p.packageId) === String(buyId) || String(p.id) === String(buyId)
    );
    if (!target) return;
    autoBuyRef.current = true;
    setDataType(target.isUnlimited ? 'unlimited' : 'fixed');
    setSelectedPackage(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topupPackages, searchParams]);

  // Reset duration index when data type changes (to prevent out of bounds)
  useEffect(() => {
    setSelectedDurationIndex(0);
  }, [dataType]);

  // Fetch the user's saved cards (if any) so we can offer one-tap top-up,
  // mirroring the purchase checkout.
  useEffect(() => {
    if (!user?.email) { setSavedCard(null); setSavedCards([]); setSelectedCardId(''); return; }
    let active = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/payment-methods/${encodeURIComponent(user.email)}`);
        if (!active) return;
        const list = r.data?.cards || [];
        setSavedCards(list);
        const def = list.find((c) => c.is_default) || list[0] || null;
        setSavedCard(def);
        setSelectedCardId(def?.revolut_payment_method_id || '');
      } catch (e) { /* no saved card — ignore */ }
    })();
    return () => { active = false; };
  }, [user?.email]);

  // Create Revolut order when package is selected
  useEffect(() => {
    const createRevolutOrder = async () => {
      if (!user || !selectedPackage || revolutToken) return;

      const basePrice = parseFloat(convertPrice(selectedPackage.price).toFixed(2));
      const priceInUserCurrency = parseFloat((basePrice * (1 - promoDiscount)).toFixed(2));
      const currencyCode = currentCurrency?.code || 'AUD';

      try {
        const response = await axios.post(`${API}/revolut/create-order`, {
          amount: priceInUserCurrency,
          currency: currencyCode,
          description: `Top-Up: ${selectedPackage.name}`,
          user_email: user.email,
          plan_id: selectedPackage.packageId,
          country_id: existingEsim?.country_id || 'TOPUP',
          promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null,
        });

        if (response.data.success) {
          setRevolutToken(response.data.token);
          setRevolutOrderId(response.data.order_id);
        }
      } catch (error) {
        console.error('Error creating Revolut order:', error);
        setPaymentError('Failed to initialize payment. Please try again.');
      }
    };

    createRevolutOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedPackage, existingEsim, convertPrice, currentCurrency?.code, promoDiscount, promoSuccess]);

  // Reset Revolut order when package or promo changes
  useEffect(() => {
    setRevolutToken(null);
    setRevolutOrderId(null);
  }, [selectedPackage?.id, promoDiscount]);

  // Promo code handlers
  const handleApplyPromo = async () => {
    const code = (promoCode || '').trim();
    if (!code) return;
    setPromoError('');
    setPromoSuccess(false);
    setPromoIsReferral(false);
    setPromoReferrerInfo(null);
    const planType = existingEsim?.isRegion ? 'regional' : 'local';
    const result = await getDiscountForCode(code, user?.email, planType, existingEsim?.country_id);
    if (result?.success && result.discount > 0) {
      setPromoDiscount(result.discount);
      setPromoSuccess(true);
      setPromoIsReferral(!!result.isReferral);
      if (result.isReferral) {
        setPromoReferrerInfo({ name: result.referrerName, email: result.referrerEmail });
      }
    } else {
      setPromoError(result?.message || 'Invalid or expired code');
      setPromoDiscount(0);
    }
  };

  const handleRemovePromo = () => {
    setPromoCode('');
    setPromoDiscount(0);
    setPromoSuccess(false);
    setPromoError('');
    setPromoIsReferral(false);
    setPromoReferrerInfo(null);
  };

  // One-tap top-up charging the user's saved card (mirrors the purchase flow's
  // payWithSavedCard → then runs the SAME /esim/topup provisioning path).
  const payWithSavedCard = async () => {
    if (savedCardBusy || !user?.email || !selectedPackage) return;
    setSavedCardBusy(true);
    setSavedCardError('');
    try {
      const resp = await axios.post(`${API}/esim/topup-saved-card`, {
        user_email: user.email,
        iccid,
        package_id: selectedPackage.packageId,
        payment_method_id: selectedCardId || savedCard?.revolut_payment_method_id || '',
        price_aud: (convertToAud ? convertToAud(selectedPackage.price) : selectedPackage.price) * (1 - promoDiscount),
        currency: currentCurrency?.code || 'AUD',
        promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null,
        browser_env: {
          tz_offset: -new Date().getTimezoneOffset(),
          color_depth: window.screen?.colorDepth || 24,
          screen_width: window.screen?.width || 1920,
          screen_height: window.screen?.height || 1080,
          browser_url: window.location.href,
        },
      });
      if (resp.data?.success && resp.data.session_id) {
        await handlePaymentSuccess(resp.data.session_id);
      } else if (resp.data?.mode === 'processing') {
        // Payment still settling at Revolut — don't ask for the card again
        // (would double-charge). Show the graceful "finalising" screen.
        setProvisioningPending(true);
      } else {
        setSavedCardError("We couldn't charge your saved card automatically — please enter your card below to complete payment.");
      }
    } catch (e) {
      setSavedCardError(e.response?.data?.detail || 'Could not use your saved card. Please enter your card below.');
    } finally {
      setSavedCardBusy(false);
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = async (paymentIntentId) => {
    setPaymentSuccess(true);
    setIsProcessingTopup(true);

    // Save the card for future use only when the user ticked "Save this card"
    // (parity with the purchase checkout). Resolves in the background.
    if (saveCard && typeof paymentIntentId === 'string' && paymentIntentId.startsWith('revolut-') && user?.email) {
      const orderId = paymentIntentId.replace('revolut-', '');
      if (orderId) {
        setCardSaveStatus('saving');
        axios.post(`${API}/revolut/save-payment-method`, {
          revolut_order_id: orderId,
          user_email: user.email,
        })
          .then(() => setCardSaveStatus('saved'))
          .catch(err => { console.warn('Save card still processing:', err?.message); setCardSaveStatus('saved'); });
      }
    }

    try {
      const response = await axios.post(`${API}/esim/topup`, {
        iccid: iccid,
        package_id: selectedPackage.packageId,
        user_email: user.email,
        session_id: paymentIntentId,
        data_amount: selectedPackage.dataAmount,
        duration_days: selectedPackage.days,
        plan_name: selectedPackage.name,
        price: selectedPackage.price,
        promo_code: promoSuccess && promoCode ? promoCode.toUpperCase() : null,
      });

      if (response.data.success) {
        setTopupResult(response.data);
      } else {
        setPaymentError('Top-up processing failed. Please contact support.');
      }
    } catch (error) {
      console.error('Error processing top-up:', error);
      setPaymentError('Top-up processing failed. Please contact support.');
    } finally {
      setIsProcessingTopup(false);
    }
  };

  const handlePaymentError = (error) => {
    setPaymentError(error);
  };

  // In the native app in-app browser, after a successful top-up or a
  // "finalising" (processing) outcome, bounce the browser to the app's return
  // URL so it auto-closes and the user lands back on My eSIMs inside the app
  // instead of being stranded on the web success screen.
  useEffect(() => {
    if (!embedded) return;
    if (!(paymentSuccess && topupResult) && !provisioningPending) return;
    const timer = setTimeout(() => {
      const ret = getReturnUrl();
      if (!ret) return;
      // Carry the success payload back to the native app so it can show a
      // confirmation popup with the updated data balance without having to
      // re-fetch my eSIMs first.
      const params = new URLSearchParams();
      params.set('topup', provisioningPending ? 'pending' : 'success');
      if (iccid) params.set('iccid', String(iccid));
      if (selectedPackage) {
        params.set('added', String(selectedPackage.dataAmount || 0));
        params.set('days', String(selectedPackage.days || 0));
        params.set('unlimited', selectedPackage.isUnlimited ? '1' : '0');
      }
      const newTotal = topupResult?.new_data_amount
        ?? ((esimUsage?.remaining_data_gb || 0) + (selectedPackage?.dataAmount || 0));
      if (newTotal != null) params.set('newTotal', String(newTotal));
      const sep = ret.includes('?') ? '&' : '?';
      window.location.href = `${ret}${sep}${params.toString()}`;
    }, 2500);
    return () => clearTimeout(timer);
  }, [embedded, paymentSuccess, topupResult, provisioningPending, iccid, selectedPackage, esimUsage]);

  if (isCheckingAuth || !user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400" />
      </div>
    );
  }

  // Shared summary card — rendered inline (above Payment) in the native app
  // in-app browser so the user sees what they're buying first, and in the
  // sticky right column on the web.
  const summaryCard = (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-neutral-800">
      <h3 className="font-bold text-gray-900 dark:text-neutral-100 mb-4">Top-Up Summary</h3>
      {selectedPackage ? (
        <>
          <div className="space-y-3 pb-4 border-b border-gray-100 dark:border-neutral-800">
            <div className="flex justify-between items-center gap-3">
              <span className="text-gray-600 dark:text-neutral-400 shrink-0">Data Package</span>
              <span className="font-semibold text-right min-w-0 truncate">
                {selectedPackage.isUnlimited
                  ? 'Unlimited'
                  : `${selectedPackage.dataAmount >= 1 ? selectedPackage.dataAmount.toFixed(0) : selectedPackage.dataAmount.toFixed(2)}GB`}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3">
              <span className="text-gray-600 dark:text-neutral-400 shrink-0">Validity</span>
              <span className="font-semibold text-right min-w-0 truncate">
                {selectedPackage.days > 0 ? `${selectedPackage.days} Days` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between items-center gap-3">
              <span className="text-gray-600 dark:text-neutral-400 shrink-0">Plan</span>
              <span className="font-semibold text-sm text-right min-w-0 truncate">
                {selectedPackage.name}
              </span>
            </div>
          </div>
          <div className="py-4 border-b border-gray-100 dark:border-neutral-800 space-y-2">
            {promoSuccess && promoDiscount > 0 ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-neutral-400">Subtotal</span>
                  <span className="font-semibold line-through text-gray-400">{formatPrice(selectedPackage.price)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-emerald-600 dark:text-emerald-400 font-semibold">Promo discount</span>
                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                    −{formatPrice(selectedPackage.price * promoDiscount)}
                  </span>
                </div>
              </>
            ) : null}
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="text-emerald-600 dark:text-emerald-400">
                {formatPrice(selectedPackage.price * (1 - promoDiscount))}
              </span>
            </div>
          </div>
          <div className="pt-4 space-y-2">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-400">
              <Check size={16} className="text-emerald-500" />
              <span>Instant activation</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-400">
              <Check size={16} className="text-emerald-500" />
              <span>Added to existing balance</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-neutral-400">
              <Check size={16} className="text-emerald-500" />
              <span>No new QR code needed</span>
            </div>
          </div>
        </>
      ) : (
        <p className="text-gray-500 dark:text-neutral-500 text-center py-8">Select a package to continue</p>
      )}
    </div>
  );

  // Provisioning-pending view — the saved-card charge is still settling at
  // Revolut. Reassure the customer (no scary error); the top-up finalises in
  // the background and the auto-refund sweep reconciles if it can't.
  if (provisioningPending) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950" data-testid="topup-pending-view">
        {!embedded && <Navbar />}
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
              <CheckCircle className="w-9 h-9 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-3">
              Payment received — finalising your top-up
            </h1>
            <p className="text-gray-600 dark:text-neutral-400 leading-relaxed mb-6">
              Your payment went through. We&apos;re adding the data to your eSIM now — it&apos;ll
              update in <strong>My eSIMs</strong> within a few minutes. If we can&apos;t finalise it,
              we&apos;ll automatically refund you — no action needed.
            </p>
            <button
              data-testid="topup-pending-dashboard-btn"
              onClick={() => {
                const ret = embedded ? getReturnUrl() : null;
                if (ret) { window.location.href = ret; } else { navigate('/dashboard'); }
              }}
              className="px-8 py-4 bg-black text-white dark:bg-white dark:text-black rounded-xl font-bold"
            >
              Back to My eSIMs
            </button>
          </div>
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  // Success state
  if (paymentSuccess && topupResult) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        {!embedded && <Navbar />}
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h1 className="text-3xl font-black text-gray-900 dark:text-neutral-100 mb-4">Top-Up Successful!</h1>
            <p className="text-gray-600 dark:text-neutral-400 mb-8">
              Your eSIM has been topped up with {selectedPackage?.isUnlimited 
                ? 'Unlimited Data' 
                : `${selectedPackage?.dataAmount >= 1 ? selectedPackage?.dataAmount.toFixed(0) : selectedPackage?.dataAmount?.toFixed(2)}GB`}
            </p>
            
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-neutral-800 mb-8">
              <h3 className="font-bold text-gray-900 dark:text-neutral-100 mb-4">Updated Balance</h3>
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-neutral-500">Previous</p>
                  <p className="text-2xl font-bold text-gray-400 dark:text-neutral-500">
                    {esimUsage?.remaining_data_gb?.toFixed(2) || existingEsim?.data_amount || 0}GB
                  </p>
                </div>
                <Plus className="text-emerald-500" size={24} />
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-neutral-500">Added</p>
                  <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {selectedPackage?.dataAmount >= 1 ? selectedPackage?.dataAmount.toFixed(0) : selectedPackage?.dataAmount?.toFixed(2)}GB
                  </p>
                </div>
                <span className="text-2xl font-bold text-gray-400 dark:text-neutral-500">=</span>
                <div className="text-center">
                  <p className="text-sm text-gray-500 dark:text-neutral-500">New Total</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-neutral-100">
                    {topupResult.new_data_amount?.toFixed(2) || 
                      ((esimUsage?.remaining_data_gb || 0) + (selectedPackage?.dataAmount || 0)).toFixed(2)}GB
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                const ret = embedded ? getReturnUrl() : null;
                if (ret) { window.location.href = ret; } else { navigate('/dashboard'); }
              }}
              className="w-full py-4 bg-black text-white rounded-xl font-bold hover:bg-gray-800 transition-colors"
            >
              Back to My eSIMs
            </button>
          </div>
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  // Processing state
  if (isProcessingTopup) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
        {!embedded && <Navbar />}
        <div className="pt-32 pb-20 px-4">
          <div className="max-w-lg mx-auto text-center">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-600 dark:text-emerald-400 mx-auto mb-6" />
            <h1 className="text-2xl font-black text-gray-900 dark:text-neutral-100 mb-4">Processing Your Top-Up</h1>
            <p className="text-gray-600 dark:text-neutral-400">Please wait while we add data to your eSIM...</p>
          </div>
        </div>
        {!embedded && <Footer />}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 overflow-x-hidden">
      {!embedded && <Navbar />}

      {/* Hero — hidden entirely in the native app in-app browser (the mobile
          app already shows its own header). On the web we use the country's
          cover image to match the destination page, with a dark overlay for
          contrast. */}
      {!embedded && (
      <section className="relative pt-24 pb-10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={existingEsim?.country?.image || 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=1920&q=80'}
            alt={existingEsim?.country_name || 'Destination'}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/55 to-black/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-white/90 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to My eSIMs</span>
          </button>
          <h1 className="text-3xl sm:text-4xl font-black text-white drop-shadow">Top Up Your eSIM</h1>
        </div>
      </section>
      )}

      {/* Embedded top padding — the hero was providing it on the web. */}
      {embedded && <div className="pt-4" />}

      {/* Main Content */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Package Selection */}
            <div className="lg:col-span-2 space-y-6">
              {/* Current eSIM Info - Always show if we have usage data */}
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-neutral-800">
                <div className="flex items-center gap-4 mb-4">
                  {/* Flag or Earth Icon */}
                  {existingEsim?.isRegion ? (
                    <div className="w-12 h-9 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-lg flex items-center justify-center">
                      <Globe size={20} className="text-white" />
                    </div>
                  ) : existingEsim?.country?.flag ? (
                    <img 
                      src={existingEsim.country.flag} 
                      alt={existingEsim.country_name} 
                      className="w-12 h-9 object-cover rounded-lg shadow" 
                    />
                  ) : (
                    <div className="w-12 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
                      <Wifi size={20} className="text-white" />
                    </div>
                  )}
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-neutral-100">{existingEsim?.country_name || 'Current eSIM'}</h3>
                    <p className="text-sm text-gray-500 dark:text-neutral-500">
                      {existingEsim?.isRegion ? 'Regional Plan' : existingEsim?.planName || 'eSIM Plan'}
                    </p>
                  </div>
                  {/* Status Badge */}
                  {esimUsage && (
                    <span className={`ml-auto px-3 py-1 rounded-full text-xs font-semibold ${
                      esimUsage.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                      esimUsage.status === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {esimUsage.status}
                    </span>
                  )}
                </div>
                
                {loadingUsage ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-600 dark:text-emerald-400" />
                    <span className="ml-2 text-gray-500 dark:text-neutral-500">Loading eSIM data...</span>
                  </div>
                ) : esimUsage ? (
                  <div className="flex items-center flex-wrap gap-x-6 gap-y-4 pt-4 border-t border-gray-100 dark:border-neutral-800">
                    <div className="flex items-center gap-3 min-w-0">
                      <Wifi className="text-emerald-600 dark:text-emerald-400 shrink-0" size={24} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 dark:text-neutral-500">Data Remaining</p>
                        <p className="text-2xl font-bold text-gray-900 dark:text-neutral-100">
                          {esimUsage.remaining_data_gb?.toFixed(2) || '0.00'}GB
                        </p>
                        <p className="text-xs text-gray-400 dark:text-neutral-500">
                          of {esimUsage.allowed_data_gb?.toFixed(2) || '0'}GB total
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 min-w-0">
                      <Clock className="text-gray-400 dark:text-neutral-500 shrink-0" size={24} />
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500 dark:text-neutral-500">Expires</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-neutral-100">
                          {esimUsage.expired_at 
                            ? new Date(esimUsage.expired_at * 1000).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short', 
                                day: 'numeric' 
                              })
                            : 'Not activated yet'}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 pt-4 border-t border-gray-100 dark:border-neutral-800 text-gray-500 dark:text-neutral-500">
                    <AlertCircle size={18} />
                    <span>Unable to fetch eSIM usage data</span>
                  </div>
                )}
              </div>

              {/* Filters */}
              {!(directCheckoutRequested && selectedPackage) && (
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-gray-100 dark:border-neutral-800 shadow-sm">
                {/* Data Type Buttons */}
                <div className="flex gap-3 mb-6">
                  <button
                    onClick={() => setDataType('fixed')}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-sm transition-all ${
                      dataType === 'fixed'
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid="filter-fixed-gb"
                  >
                    <Wifi size={18} />
                    Fixed GB
                  </button>
                  <button
                    onClick={() => setDataType('unlimited')}
                    className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-sm transition-all ${
                      dataType === 'unlimited'
                        ? 'bg-black text-white shadow-lg'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid="filter-unlimited"
                  >
                    <Infinity size={18} />
                    Unlimited Data
                  </button>
                </div>

                {/* Duration Slider - Clean minimal design */}
                {availableDurations.length > 1 && availableDurations[0] > 0 && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className="text-sm font-medium text-gray-600 dark:text-neutral-400">Validity Period</label>
                      <span className="text-lg font-black text-gray-900 dark:text-neutral-100">
                        {selectedDuration} {selectedDuration === 1 ? 'Day' : 'Days'}
                      </span>
                    </div>
                    
                    {/* Slider */}
                    <div className="relative pt-1">
                      <input
                        type="range"
                        min={0}
                        max={availableDurations.length - 1}
                        value={selectedDurationIndex}
                        onChange={(e) => setSelectedDurationIndex(parseInt(e.target.value))}
                        className="w-full h-1.5 bg-gray-200 dark:bg-neutral-700 rounded-full appearance-none cursor-pointer"
                        style={{
                          background: `linear-gradient(to right, #10b981 0%, #10b981 ${(selectedDurationIndex / Math.max(availableDurations.length - 1, 1)) * 100}%, #e5e7eb ${(selectedDurationIndex / Math.max(availableDurations.length - 1, 1)) * 100}%, #e5e7eb 100%)`
                        }}
                        data-testid="duration-slider"
                      />
                      {/* Custom thumb styles */}
                      <style>{`
                        input[type="range"]::-webkit-slider-thumb {
                          -webkit-appearance: none;
                          appearance: none;
                          width: 20px;
                          height: 20px;
                          background: #10b981;
                          border-radius: 50%;
                          cursor: pointer;
                          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                        }
                        input[type="range"]::-moz-range-thumb {
                          width: 20px;
                          height: 20px;
                          background: #10b981;
                          border-radius: 50%;
                          cursor: pointer;
                          border: none;
                          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                        }
                      `}</style>
                    </div>
                    
                    {/* Key markers */}
                    <div className="flex justify-between text-xs text-gray-400 dark:text-neutral-500 px-1">
                      {(() => {
                        const durations = availableDurations;
                        const len = durations.length;
                        if (len <= 5) {
                          return durations.map(d => <span key={d}>{d}d</span>);
                        }
                        // Show key markers: first, 25%, 50%, 75%, last
                        const markers = [
                          durations[0],
                          durations[Math.floor(len * 0.25)],
                          durations[Math.floor(len * 0.5)],
                          durations[Math.floor(len * 0.75)],
                          durations[len - 1]
                        ];
                        const uniqueMarkers = [...new Set(markers)];
                        return uniqueMarkers.map(d => <span key={d}>{d}d</span>);
                      })()}
                    </div>
                  </div>
                )}

                {/* Reset Filter */}
                {dataType !== 'all' && (
                  <button
                    onClick={() => { setDataType('all'); setSelectedDurationIndex(0); }}
                    className="mt-4 text-sm text-gray-500 dark:text-neutral-500 hover:text-emerald-600 underline"
                  >
                    Reset filters
                  </button>
                )}
              </div>
              )}

              {/* Available Top-Up Packages */}
              {!(directCheckoutRequested && selectedPackage) && (
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-neutral-800">
                <h3 className="font-bold text-gray-900 dark:text-neutral-100 mb-4">
                  Available Top-Up Packages
                  {filteredPackages.length > 0 && (
                    <span className="text-sm font-normal text-gray-500 dark:text-neutral-500 ml-2">
                      ({filteredPackages.length} {filteredPackages.length === 1 ? 'package' : 'packages'})
                    </span>
                  )}
                </h3>
                
                {loadingPackages ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400" />
                  </div>
                ) : filteredPackages.length === 0 ? (
                  <div className="text-center py-12 px-4">
                    <AlertCircle className="w-12 h-12 text-gray-400 dark:text-neutral-500 mx-auto mb-4" />
                    {topupPackages.length === 0 ? (
                      <>
                        <p className="text-gray-700 dark:text-neutral-300 font-semibold mb-2">Unfortunately this package cannot be topped up at this time</p>
                        <p className="text-sm text-gray-500 dark:text-neutral-500 mb-6 max-w-md mx-auto">This may be due to the package being expired, or top up functionality may not be available on this particular eSIM. Please proceed to purchase a new Connectify eSIM for more data.</p>
                        <button
                          onClick={() => navigate('/destinations')}
                          className="px-6 py-3 bg-black text-white rounded-full font-bold hover:bg-gray-800 transition-colors"
                          data-testid="buy-new-esim-btn"
                        >
                          Buy new eSIM
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-gray-600 dark:text-neutral-400">No packages match your filters</p>
                        <p className="text-sm text-gray-500 dark:text-neutral-500 mt-1">Try adjusting your filters</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="grid gap-3">
                    {filteredPackages.map((pkg) => (
                      <button
                        key={pkg.id}
                        onClick={() => setSelectedPackage(pkg)}
                        className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                          selectedPackage?.id === pkg.id
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-400'
                            : 'border-gray-200 dark:border-neutral-700 hover:border-emerald-300 dark:hover:border-emerald-500 bg-white dark:bg-neutral-900'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              selectedPackage?.id === pkg.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 dark:bg-neutral-800 text-gray-600 dark:text-neutral-300'
                            }`}>
                              <Plus size={24} />
                            </div>
                            <div>
                              <p className="font-bold text-gray-900 dark:text-neutral-100">
                                {pkg.isUnlimited ? 'Unlimited Data' : `${pkg.dataAmount >= 1 ? pkg.dataAmount.toFixed(0) : pkg.dataAmount.toFixed(2)}GB`}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-neutral-500">
                                {pkg.days > 0 ? `${pkg.days} Days validity` : pkg.name}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatPrice(pkg.price)}</p>
                            {selectedPackage?.id === pkg.id && (
                              <Check className="text-emerald-500 ml-auto" size={20} />
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              )}

              {/* In the native app in-app browser, show the summary ABOVE the
                  card payment so users see what they're paying for first. */}
              {embedded && selectedPackage && summaryCard}

              {/* Promo / referral code */}
              {selectedPackage && (
                <CheckoutPromoSection
                  promoCode={promoCode}
                  setPromoCode={setPromoCode}
                  promoSuccess={promoSuccess}
                  promoError={promoError}
                  setPromoError={setPromoError}
                  discount={promoDiscount}
                  isReferral={promoIsReferral}
                  referrerInfo={promoReferrerInfo}
                  onApply={handleApplyPromo}
                  onRemove={handleRemovePromo}
                />
              )}

              {/* Payment Section */}
              {selectedPackage && (
                <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-neutral-800">
                  <h3 className="font-bold text-gray-900 dark:text-neutral-100 mb-4 flex items-center gap-2">
                    <CreditCard size={20} /> Complete Payment
                  </h3>

                  {revolutToken ? (
                    <div className="space-y-4">
                      <RevolutPaymentForm
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        formatPrice={formatPrice}
                        total={convertPrice(selectedPackage.price) * (1 - promoDiscount)}
                        totalInAud={(convertToAud ? convertToAud(selectedPackage.price) : selectedPackage.price) * (1 - promoDiscount)}
                        revolutToken={revolutToken}
                        revolutOrderId={revolutOrderId}
                        user={user}
                        country={{ id: existingEsim?.country_id, name: existingEsim?.country_name || existingEsim?.country?.name }}
                        plan={{ id: selectedPackage.packageId, packageId: selectedPackage.packageId, name: `Top-Up: ${selectedPackage.name}` }}
                        currentCurrency={currentCurrency}
                        description={`Top-Up: ${selectedPackage.name}`}
                        savePaymentMethod={false}
                      />
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-12" data-testid="topup-payment-initializing">
                      <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-neutral-500" />
                      <span className="ml-3 text-gray-600 dark:text-neutral-400">Initializing secure payment...</span>
                    </div>
                  )}
                </div>
              )}

              {paymentError && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                  <AlertCircle className="text-red-600 dark:text-red-400" size={24} />
                  <p className="text-red-700 dark:text-red-300">{paymentError}</p>
                </div>
              )}
            </div>

            {/* Right Column - Order Summary (hidden in embedded mode; it's
                rendered inline above the Payment section instead). */}
            {!embedded && (
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                {summaryCard}
              </div>
            </div>
            )}
          </div>
        </div>
      </section>

      {!embedded && <Footer />}
    </div>
  );
};

export default TopUp;
